import cloneDeep from "lodash-es/cloneDeep";
import { unwrapNodes } from "@/helpers/graphql";

const state = {
  product: {},
  selectedOptions: [],
  selectedVariant: null,
  defaultVariant: null,
  openSizeGuide: false,
  openQuickLook: false,
};

const getters = {
  selectedOptionsWithoutId: (state) => {
    return state.selectedOptions.map(({ name, value }) => ({
      name,
      value,
    }));
  },
  productOptions: (state) => {
    return state.product.options;
  },
  variant: (state) => {
    return state.selectedVariant || state.defaultVariant;
  },
  price: (state) => {
    return state.product.priceRange.minVariantPrice;
  },
  originalPrice: (state) => {
    return state.product.compareAtPriceRange.minVariantPrice;
  },
  isOnSale: (_, getters) => {
    return (
      parseFloat(getters.originalPrice.amount) >
      parseFloat(getters.price.amount)
    );
  },
  salePreviewPrice: (state) => {
    return state.product.salePreviewPrice?.value || null;
  },
  variants: (state) => {
    const variants = unwrapNodes(state.product?.variants?.edges) || [];

    const sizeOnlyVariants = variants.map((variant) => {
      const title = variant.title.split(" / ")[0];

      return {
        ...variant,
        title,
      };
    });

    return sizeOnlyVariants || null;
  },
  preOrder: (state) => {
    const preorder = state.selectedVariant?.randbPreorder?.value;
    if (!preorder) return false;

    return JSON.parse(preorder);
  },
  sellingPlanAllocation: (state, getters) => {
    const sellingPlanAllocations =
      unwrapNodes(state.selectedVariant?.sellingPlanAllocations?.edges) || [];

    const sellingPlanAllocation = sellingPlanAllocations.find((allocation) => {
      return allocation.sellingPlan?.id === getters.preOrder.sellingPlanId;
    });

    return sellingPlanAllocation;
  },
  availableForPreOrder: (state, getters) => {
    if (!getters.preOrder) return false;

    const { enabled = false, limit = 0, reserved = 0 } = getters.preOrder;
    const inventory = state.selectedVariant.quantityAvailable || 0;

    return enabled && inventory <= 0 && reserved < limit;
  },
};

const mutations = {
  setSelectedOptions(state, value) {
    state.selectedOptions = value;
  },
  setProduct(state, value) {
    state.product = value;
  },
  setSelectedVariant(state, value) {
    state.selectedVariant = value;
  },
  setDefaultVariant(state, value) {
    state.defaultVariant = value;
  },
  setOpenSizeGuide(state, value) {
    state.openSizeGuide = value;
  },
  setOpenQuickLook(state, value) {
    state.openQuickLook = value;
  },
};

const actions = {
  changeProductOption({ commit, state, dispatch }, option) {
    const selectedOptions = cloneDeep(state.selectedOptions);
    const index = selectedOptions.findIndex(
      (selectedOption) => selectedOption.id === option.id
    );

    if (index === -1) {
      selectedOptions.push(option);
    } else {
      selectedOptions[index] = option;
    }

    commit("setSelectedOptions", selectedOptions);

    dispatch("updateProductUrl");
  },
  setSelectedOptions({ commit }, options) {
    commit("setSelectedOptions", options);
  },
  updateProductUrl({ state, getters }) {
    const selectedOptions = cloneDeep(state.selectedOptions);
    const url = new URL(window.location);
    selectedOptions.forEach((selectedOption) => {
      const actualOption = getters.productOptions.find((productOption) => {
        return productOption.id === selectedOption.id;
      });

      if (actualOption.values.length > 1) {
        url.searchParams.set(
          selectedOption.name.toLowerCase(),
          selectedOption.value.toLowerCase()
        );
      }
    });

    window.history.replaceState(null, "", url.toString());
  },
  updateProductUrlV2({ state }) {
    const url = new URL(window.location);
    let urlChanged = false;

    if (state.selectedVariant) {
      const id = state.selectedVariant.id.split(
        "gid://shopify/ProductVariant/"
      )[1];
      if (url.searchParams.get("variant") !== id) {
        url.searchParams.set("variant", id);
        urlChanged = true;
      }
    }

    if (urlChanged) {
      window.history.replaceState(null, "", url.toString());
    }
  },
  setProduct({ commit }, product) {
    commit("setProduct", product);
  },

  setSelectedVariant({ commit }, value) {
    commit("setSelectedVariant", value);
  },
  setDefaultVariant({ commit }, value) {
    commit("setDefaultVariant", value);
  },
  setOpenSizeGuide({ commit }, value) {
    commit("setOpenSizeGuide", value);
  },
  setOpenQuickLook({ commit }, value) {
    commit("setOpenQuickLook", value);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
